import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { CheckCircle2, Info, X, AlertTriangle } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export type ToastVariant = 'success' | 'info' | 'error';

interface ToastItem {
    id:      number;
    message: string;
    variant: ToastVariant;
}

interface ToastContextValue {
    /** Show a transient toast. Returns nothing — fire and forget. */
    show: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_ICON = {
    success: CheckCircle2,
    info:    Info,
    error:   AlertTriangle,
} as const;

const VARIANT_ACCENT: Record<ToastVariant, string> = {
    success: 'text-emerald-600',
    info:    'text-brand-sky',
    error:   'text-red-600',
};

/**
 * Minimal toast system — a context + a fixed viewport. Self-contained (no
 * Radix wiring) so any component can call `useToast().show(...)`. Toasts
 * auto-dismiss after ~3.5s and stack bottom-right.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const nextId = useRef(1);

    const dismiss = useCallback((id: number) => {
        setToasts((cur) => cur.filter((t) => t.id !== id));
    }, []);

    const show = useCallback(
        (message: string, variant: ToastVariant = 'info') => {
            const id = nextId.current++;
            setToasts((cur) => [...cur, { id, message, variant }]);
            window.setTimeout(() => dismiss(id), 3_500);
        },
        [dismiss],
    );

    const value = useMemo(() => ({ show }), [show]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="pointer-events-none fixed bottom-4 right-4 z-[1200] flex w-full max-w-sm flex-col gap-2">
                {toasts.map((t) => {
                    const Icon = VARIANT_ICON[t.variant];
                    return (
                        <div
                            key={t.id}
                            className="pointer-events-auto flex items-start gap-3 rounded-xl border border-ink-100 bg-white p-3 shadow-pop"
                        >
                            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', VARIANT_ACCENT[t.variant])} strokeWidth={2.25} />
                            <span className="flex-1 text-sm text-brand-navy">{t.message}</span>
                            <button
                                type="button"
                                onClick={() => dismiss(t.id)}
                                className="text-ink-300 transition hover:text-ink-700"
                                aria-label="Dismiss"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
}
